import { format, isToday, isYesterday, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays, subWeeks, subMonths } from 'date-fns';
import i18n from '../i18n';

export function toDatetimeLocal(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : date.toDate?.() ?? new Date(date);
  return format(d, "yyyy-MM-dd'T'HH:mm");
}

/** datetime-local with second precision — used when ending active tasks. */
export function toDatetimeLocalSeconds(date) {
  if (!date) return '';
  const d = date instanceof Date ? date : date.toDate?.() ?? new Date(date);
  return format(d, "yyyy-MM-dd'T'HH:mm:ss");
}

export function fromDatetimeLocal(str) {
  if (!str) return null;
  return new Date(str);
}

/** Elapsed whole seconds between two timestamps (floor, never negative). */
export function elapsedSecondsBetween(startTime, endTime) {
  if (!startTime || !endTime) return 0;
  const start = startTime instanceof Date ? startTime : startTime.toDate?.() ?? new Date(startTime);
  const end = endTime instanceof Date ? endTime : endTime.toDate?.() ?? new Date(endTime);
  return Math.max(0, Math.floor((end.getTime() - start.getTime()) / 1000));
}

/** Below this elapsed time, production users are offered a discard path. */
export const SHORT_TASK_DISCARD_THRESHOLD_SECONDS = 90;

export function formatTime(date) {
  if (!date) return '—';
  const d = date instanceof Date ? date : date.toDate?.() ?? new Date(date);
  return format(d, 'h:mm a');
}

export function formatDateTime(date) {
  if (!date) return '—';
  const d = date instanceof Date ? date : date.toDate?.() ?? new Date(date);
  return format(d, 'MMM d, yyyy h:mm a');
}

export function formatSmartDateTime(date) {
  if (!date) return '—';
  const d = date instanceof Date ? date : date.toDate?.() ?? new Date(date);
  const timeStr = format(d, 'h:mm a');
  if (isToday(d)) return i18n.t('time.todayTime', { time: timeStr });
  if (isYesterday(d)) return i18n.t('time.yesterdayTime', { time: timeStr });
  return format(d, 'MMM d, h:mm a');
}

export function formatDate(date) {
  if (!date) return '—';
  const d = date instanceof Date ? date : date.toDate?.() ?? new Date(date);
  return format(d, 'MMM d, yyyy');
}

export function getDateRange(preset) {
  const now = new Date();
  switch (preset) {
    case 'today':
      return { start: startOfDay(now), end: endOfDay(now) };
    case 'yesterday': {
      const y = subDays(now, 1);
      return { start: startOfDay(y), end: endOfDay(y) };
    }
    case 'thisWeek':
      return { start: startOfWeek(now, { weekStartsOn: 1 }), end: endOfWeek(now, { weekStartsOn: 1 }) };
    case 'thisMonth':
      return { start: startOfMonth(now), end: endOfMonth(now) };
    case 'last3Months':
      return { start: startOfDay(subMonths(now, 3)), end: endOfDay(now) };
    case 'last6Months':
      return { start: startOfDay(subMonths(now, 6)), end: endOfDay(now) };
    case 'last365Days':
      return { start: startOfDay(subDays(now, 365)), end: endOfDay(now) };
    default:
      return { start: startOfDay(now), end: endOfDay(now) };
  }
}

/**
 * The comparable previous period for a given preset, so the dashboard can show
 * "vs last month / last week / yesterday" deltas.
 *
 * Calendar presets (today/yesterday/week/month) compare against the same
 * elapsed slice of the prior calendar period — i.e. month-to-date vs
 * last-month-to-date — so a partial current month isn't unfairly compared to a
 * full previous month. Rolling ranges (last3Months, etc.) compare against the
 * equal-length window immediately preceding them.
 */
export function getComparisonRange(preset) {
  const now = new Date();
  const cur = getDateRange(preset);
  switch (preset) {
    case 'today': {
      const y = subDays(now, 1);
      return { start: startOfDay(y), end: endOfDay(y) };
    }
    case 'yesterday': {
      const y = subDays(now, 2);
      return { start: startOfDay(y), end: endOfDay(y) };
    }
    case 'thisWeek': {
      const elapsed = now.getTime() - cur.start.getTime();
      const ps = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
      return { start: ps, end: new Date(ps.getTime() + elapsed) };
    }
    case 'thisMonth': {
      const elapsed = now.getTime() - cur.start.getTime();
      const ps = startOfMonth(subMonths(now, 1));
      return { start: ps, end: new Date(ps.getTime() + elapsed) };
    }
    default: {
      // Rolling range: equal-length window immediately before the current one.
      const len = cur.end.getTime() - cur.start.getTime();
      const end = new Date(cur.start.getTime() - 1);
      return { start: new Date(end.getTime() - len), end };
    }
  }
}

/**
 * i18n key (under `common.*`) describing what the comparison period represents,
 * e.g. "vs last month". Falls back to a generic "previous period".
 */
export function getComparisonLabelKey(preset) {
  switch (preset) {
    case 'today': return 'vsYesterday';
    case 'yesterday': return 'vsPrevDay';
    case 'thisWeek': return 'vsLastWeek';
    case 'thisMonth': return 'vsLastMonth';
    default: return 'vsPrevPeriod';
  }
}

export function formatLastActive(date) {
  if (!date) return null;
  const d = date instanceof Date ? date : date.toDate?.() ?? new Date(date);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 2) return i18n.t('time.justNow');
  if (diffMins < 60) return i18n.t('time.minutesAgo', { count: diffMins });
  if (isToday(d)) return i18n.t('time.todayAt', { time: format(d, 'h:mm a') });
  if (isYesterday(d)) return i18n.t('time.yesterdayAt', { time: format(d, 'h:mm a') });
  if (diffDays < 7) return i18n.t('time.daysAgo', { count: diffDays });
  if (diffDays < 365) return format(d, 'MMM d');
  return format(d, 'MMM d, yyyy');
}

export function activityLevel(date) {
  if (!date) return 'never';
  const d = date instanceof Date ? date : date.toDate?.() ?? new Date(date);
  const diffDays = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (diffDays < 1) return 'today';
  if (diffDays < 8) return 'recent';
  if (diffDays < 31) return 'stale';
  return 'inactive';
}

export function elapsedSeconds(startTime) {
  if (!startTime) return 0;
  const start = startTime instanceof Date ? startTime : startTime.toDate?.() ?? new Date(startTime);
  return Math.floor((Date.now() - start.getTime()) / 1000);
}

export function formatElapsed(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}
